import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { MiscellaneousService, LangGraphInstance } from 'ldt-chat-api';
import { Log } from 'src/app/logger.service';
export { LangGraphInstance } from 'ldt-chat-api';

const STORAGE_KEY = 'langgraph_instance';

@Injectable({
  providedIn: 'root',
})
export class LangGraphInstanceService {
  private instances$ = new BehaviorSubject<LangGraphInstance[]>([]);
  private selectedInstance$ = new BehaviorSubject<string | null>(null);
  private loaded = false;
  private loading$: Observable<LangGraphInstance[]> | null = null;

  constructor(private miscService: MiscellaneousService) {
    if (this.isEnabled) {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        this.selectedInstance$.next(saved);
      }
    }
  }

  get isEnabled(): boolean {
    return !environment.production;
  }

  get instances(): Observable<LangGraphInstance[]> {
    return this.instances$.asObservable();
  }

  get selectedInstance(): Observable<string | null> {
    return this.selectedInstance$.asObservable();
  }

  get selectedInstanceValue(): string | null {
    return this.selectedInstance$.value;
  }

  loadInstances(): Observable<LangGraphInstance[]> {
    if (!this.isEnabled) {
      return of([]);
    }

    if (this.loaded) {
      return this.instances$.asObservable();
    }

    // Deduplicate concurrent calls by reusing in-flight request
    if (this.loading$) {
      return this.loading$;
    }

    this.loading$ = this.miscService.listInstances().pipe(
        map((res) => res.instances),
        tap((instances) => {
          this.instances$.next(instances);
          this.loaded = true;
          this.loading$ = null;

          // Auto-select default if nothing selected
          if (!this.selectedInstance$.value) {
            const defaultInstance = instances.find((i) => i.is_default);
            if (defaultInstance) {
              this.selectInstance(defaultInstance.name);
            }
          }
        }),
        catchError((error) => {
          Log.e('LangGraphInstanceService: loadInstances: error:', error);
          this.loading$ = null;
          return of([]);
        }),
        shareReplay(1)
      );

    return this.loading$;
  }

  selectInstance(name: string): void {
    this.selectedInstance$.next(name);
    localStorage.setItem(STORAGE_KEY, name);
  }
}
